import {
  RateLimitedLogger as DefaultRateLimitedLogger,
  type IRateLimitedLogger
} from '../RateLimitedLogger/RateLimitedLogger';
import { TelemetryTransportError } from '../TelemetryTransportError/TelemetryTransportError';
import type { AgentContext, ITelemetryRegistry, LogEvent, MetricEvent } from '../types/types';
import { LogLevel, MetricType } from '../types/types';

const QUEUE_SOFT_CAP = 1_000;
const QUEUE_HARD_CAP = 5_000;
const FLUSH_SIZE_TRIGGER = 50;
const ERROR_FLUSH_DELAY_MS = 1_000;
const PER_EVENT_MAX_BYTES = 32 * 1024;
const PER_BATCH_MAX_BYTES = 1024 * 1024;
const BACKOFF_SEQUENCE_MS = [1_000, 2_000, 4_000, 8_000, 16_000, 30_000];
const RETRY_TOTAL_CAP_MS = 5 * 60 * 1_000;

export interface IClock {
  now(): number;
  sleep(ms: number): Promise<void>;
}

export interface ITelemetryTransport {
  send(endpoint: '/v1/logs', body: { context: AgentContext; events: LogEvent[] }): Promise<void>;
  send(endpoint: '/v1/metrics', body: { context: AgentContext; events: MetricEvent[] }): Promise<void>;
}

export interface ITelemetryAgentMetrics {
  dropped_event_count: number;
  dropped_oversized_batch_count: number;
  dropped_oversized_event_count: number;
  in_flight_request_count: number;
  last_flush_at_ms: number;
  queue_high_water_mark: number;
}

export interface ITelemetryAgentOptions<TRegistry extends ITelemetryRegistry = ITelemetryRegistry> {
  clock: IClock;
  context: AgentContext;
  /**
   * When > 0, drain the queue on this cadence (ms) in addition to the size/error/identity triggers.
   * 0 or omitted disables the timer (event-driven only) — the default, preserving legacy behavior.
   */
  flushIntervalMs?: number;
  rateLimitedLogger?: IRateLimitedLogger;
  registry: TRegistry;
  transport: ITelemetryTransport;
}

type MetricNamesOfType<TRegistry extends ITelemetryRegistry, T extends MetricType> = {
  [K in keyof TRegistry['metrics']]: TRegistry['metrics'][K]['type'] extends T ? K & string : never;
}[keyof TRegistry['metrics']];

type LabelsFor<TEntry> = TEntry extends { labelKeys: readonly (infer L)[] }
  ? L extends string
    ? Record<L, string>
    : Record<string, never>
  : Record<string, never>;

type EventNamesOf<TRegistry extends ITelemetryRegistry> = keyof TRegistry['events'] & string;

export interface ITelemetryAgent<TRegistry extends ITelemetryRegistry = ITelemetryRegistry> {
  counter<K extends MetricNamesOfType<TRegistry, MetricType.COUNTER>>(
    name: K,
    value: number,
    labels?: LabelsFor<TRegistry['metrics'][K]>
  ): void;
  dispose(): void;
  flush(): Promise<void>;
  gauge<K extends MetricNamesOfType<TRegistry, MetricType.GAUGE>>(
    name: K,
    value: number,
    labels?: LabelsFor<TRegistry['metrics'][K]>
  ): void;
  getMetrics(): ITelemetryAgentMetrics;
  histogram<K extends MetricNamesOfType<TRegistry, MetricType.HISTOGRAM>>(
    name: K,
    value: number,
    labels?: LabelsFor<TRegistry['metrics'][K]>
  ): void;
  log(event: Omit<LogEvent, 'ts'> & { event?: EventNamesOf<TRegistry> }): void;
  setContext(partial: Partial<AgentContext>): Promise<void>;
}

export class TelemetryAgent<TRegistry extends ITelemetryRegistry = ITelemetryRegistry>
  implements ITelemetryAgent<TRegistry>
{
  private readonly _clock: IClock;
  private _context: AgentContext;
  private _errorFlushScheduled: boolean = false;
  private _flushInFlight: Promise<void> | null = null;
  private readonly _flushIntervalMs: number;
  private _periodicFlushActive: boolean = false;
  private readonly _logQueue: LogEvent[] = [];
  private readonly _metricQueue: MetricEvent[] = [];
  private _metrics: ITelemetryAgentMetrics = {
    dropped_event_count: 0,
    dropped_oversized_batch_count: 0,
    dropped_oversized_event_count: 0,
    in_flight_request_count: 0,
    last_flush_at_ms: 0,
    queue_high_water_mark: 0
  };
  private readonly _rateLimitedLogger: IRateLimitedLogger;
  private readonly _registry: TRegistry;
  private _softCapWarned: boolean = false;
  private readonly _transport: ITelemetryTransport;

  /**
   * @param options - Construction options.
   * @param options.clock - Injected clock for `now()` and `sleep(ms)`; production = `Date.now` + `setTimeout`.
   * @param options.context - Initial context attached to every emitted batch.
   * @param options.flushIntervalMs - When > 0, also drain the queue on this cadence (ms). Defaults to 0 (disabled).
   * @param options.rateLimitedLogger - Optional override for the rate-limited warn helper.
   * @param options.registry - The typed metric registry for compile-time name/label checks.
   * @param options.transport - Concrete transport (web HTTP, Electron IPC, Node-fetch, etc.).
   */
  constructor(options: ITelemetryAgentOptions<TRegistry>) {
    this._clock = options.clock;
    this._context = { ...options.context };
    this._flushIntervalMs = options.flushIntervalMs ?? 0;
    this._rateLimitedLogger = options.rateLimitedLogger ?? DefaultRateLimitedLogger;
    this._registry = options.registry;
    this._transport = options.transport;
    if (this._flushIntervalMs > 0) this._startPeriodicFlush();
  }

  /**
   * Record a counter metric.
   * @param name - Metric name (must be a COUNTER entry in the registry).
   * @param value - The amount to add.
   * @param labels - Label keys must match `registry[name].labelKeys`.
   */
  counter<K extends MetricNamesOfType<TRegistry, MetricType.COUNTER>>(
    name: K,
    value: number,
    labels?: LabelsFor<TRegistry['metrics'][K]>
  ): void {
    this._recordMetric(name, MetricType.COUNTER, value, labels as Record<string, string> | undefined);
  }

  /**
   * Stop the periodic flush loop (if running). Idempotent and non-blocking — it does NOT drain the
   * queue; callers that need a final send should `await flush()` separately (eg: on app teardown).
   */
  dispose(): void {
    this._periodicFlushActive = false;
  }

  /** Drain the in-memory queues to the transport. Coalesces concurrent callers. */
  async flush(): Promise<void> {
    if (this._flushInFlight) {
      await this._flushInFlight;
      if (this._logQueue.length === 0 && this._metricQueue.length === 0) return;
    }
    this._flushInFlight = this._flushNow();
    try {
      await this._flushInFlight;
    } finally {
      this._flushInFlight = null;
    }
  }

  /**
   * Record a gauge metric.
   * @param name - Metric name (must be a GAUGE entry in the registry).
   * @param value - The current value.
   * @param labels - Label keys must match `registry[name].labelKeys`.
   */
  gauge<K extends MetricNamesOfType<TRegistry, MetricType.GAUGE>>(
    name: K,
    value: number,
    labels?: LabelsFor<TRegistry['metrics'][K]>
  ): void {
    this._recordMetric(name, MetricType.GAUGE, value, labels as Record<string, string> | undefined);
  }

  /** Returns a snapshot of internal counters and water-marks for operator inspection. */
  getMetrics(): ITelemetryAgentMetrics {
    return { ...this._metrics };
  }

  /**
   * Record a histogram observation.
   * @param name - Metric name (must be a HISTOGRAM entry in the registry).
   * @param value - The observed value.
   * @param labels - Label keys must match `registry[name].labelKeys`.
   */
  histogram<K extends MetricNamesOfType<TRegistry, MetricType.HISTOGRAM>>(
    name: K,
    value: number,
    labels?: LabelsFor<TRegistry['metrics'][K]>
  ): void {
    this._recordMetric(name, MetricType.HISTOGRAM, value, labels as Record<string, string> | undefined);
  }

  /**
   * Enqueue a log event. DEBUG events are silently dropped without enqueueing. When a machine-readable
   * `event` key is present, it must be registered in `registry.events` — an unregistered event is
   * warn-and-dropped (mirroring the unknown-metric gate), never emitted.
   * @param event - The log event; the agent stamps `ts`.
   */
  log(event: Omit<LogEvent, 'ts'> & { event?: EventNamesOf<TRegistry> }): void {
    /** DEBUG never ships. UI callers must not send it at all (see the DEBUG guard in code-integrity); this is the backstop. */
    if (event.level === LogLevel.DEBUG) return;
    if (event.event !== undefined && !this._registry.events[event.event]) {
      this._rateLimitedLogger.warn(
        'telemetry-agent:unknown-event',
        `[TelemetryAgent] Unknown event "${event.event}"; dropping.`
      );
      this._metrics.dropped_event_count += 1;
      return;
    }
    const full: LogEvent = { ...event, ts: this._clock.now() };
    if (!this._enqueueLog(full)) return;
    if (event.level === LogLevel.ERROR) this._scheduleErrorFlush();
    void this._maybeFlushBySize();
  }

  /**
   * Merge new fields into the agent context. On identity change (userId or sessionId),
   * any pending events are flushed under the OUTGOING identity before the new fields apply.
   * @param partial - Fields to merge into the context.
   */
  async setContext(partial: Partial<AgentContext>): Promise<void> {
    const identityChanged =
      (partial.userId !== undefined && partial.userId !== this._context.userId) ||
      (partial.sessionId !== undefined && partial.sessionId !== this._context.sessionId);
    if (identityChanged && (this._logQueue.length > 0 || this._metricQueue.length > 0)) await this.flush();
    this._context = { ...this._context, ...partial };
  }

  /** Test/integration hook: signal that the network came back online. */
  triggerOnlineFlush(): void {
    void this.flush();
  }

  /** Test/integration hook: signal page is unloading; consumers wire to `pagehide`/`before-quit`. */
  triggerLifecycleFlush(): void {
    void this.flush();
  }

  /**
   * Enqueue a stamped log event after enforcing per-event size limits.
   * @param event - The stamped log event.
   */
  private _enqueueLog(event: LogEvent): boolean {
    const size = this._eventSize(event);
    if (size > PER_EVENT_MAX_BYTES) {
      this._metrics.dropped_oversized_event_count += 1;
      this._metrics.dropped_event_count += 1;
      this._rateLimitedLogger.warn(
        'telemetry-agent:oversized-event',
        `[TelemetryAgent] Dropped oversized log event (${size} bytes > ${PER_EVENT_MAX_BYTES}).`
      );
      return false;
    }
    this._logQueue.push(event);
    this._updateWaterMark();
    this._enforceQueueCaps();
    return true;
  }

  /**
   * Enqueue a stamped metric event after enforcing per-event size limits.
   * @param event - The stamped metric event.
   */
  private _enqueueMetric(event: MetricEvent): boolean {
    const size = this._eventSize(event);
    if (size > PER_EVENT_MAX_BYTES) {
      this._metrics.dropped_oversized_event_count += 1;
      this._metrics.dropped_event_count += 1;
      this._rateLimitedLogger.warn(
        'telemetry-agent:oversized-event',
        `[TelemetryAgent] Dropped oversized metric event (${size} bytes > ${PER_EVENT_MAX_BYTES}).`
      );
      return false;
    }
    this._metricQueue.push(event);
    this._updateWaterMark();
    this._enforceQueueCaps();
    return true;
  }

  private _enforceQueueCaps(): void {
    const total = this._logQueue.length + this._metricQueue.length;
    if (total > QUEUE_HARD_CAP) {
      const overflow = total - QUEUE_HARD_CAP;
      for (let i = 0; i < overflow; i += 1) {
        if (this._logQueue.length > 0) this._logQueue.shift();
        else if (this._metricQueue.length > 0) this._metricQueue.shift();
        this._metrics.dropped_event_count += 1;
      }
    } else if (total >= QUEUE_SOFT_CAP && !this._softCapWarned) {
      this._softCapWarned = true;
      this._rateLimitedLogger.warn(
        'telemetry-agent:soft-cap',
        `[TelemetryAgent] Queue soft cap reached (${total} >= ${QUEUE_SOFT_CAP}).`
      );
    }
  }

  /**
   * Approximate per-event size in bytes (JSON length).
   * @param event - The event to measure.
   */
  private _eventSize(event: LogEvent | MetricEvent): number {
    try {
      return JSON.stringify(event).length;
    } catch {
      this._rateLimitedLogger.warn(
        'telemetry-agent:stringify-error',
        '[TelemetryAgent] Failed to serialize event for size check; treating as oversized.'
      );
      return PER_EVENT_MAX_BYTES + 1;
    }
  }

  /**
   * Send a batch, halving and retrying if it exceeds the per-batch byte cap.
   * @param endpoint - Wire endpoint discriminator.
   * @param events - The batch payload.
   */
  private async _flushBatch<E extends LogEvent | MetricEvent>(
    endpoint: '/v1/logs' | '/v1/metrics',
    events: E[]
  ): Promise<void> {
    if (events.length === 0) return;
    const context = { ...this._context };
    let bodySize: number;
    try {
      bodySize = JSON.stringify({ context, events }).length;
    } catch {
      this._rateLimitedLogger.warn(
        'telemetry-agent:stringify-error',
        `[TelemetryAgent] Failed to serialize batch on ${endpoint}; dropping ${events.length} events.`
      );
      this._metrics.dropped_event_count += events.length;
      return;
    }
    if (bodySize > PER_BATCH_MAX_BYTES) {
      if (events.length === 1) {
        this._metrics.dropped_oversized_batch_count += 1;
        this._metrics.dropped_event_count += 1;
        this._rateLimitedLogger.warn(
          'telemetry-agent:oversized-batch',
          `[TelemetryAgent] Dropped oversized singleton batch on ${endpoint} (${bodySize} bytes).`
        );
        return;
      }
      const mid = Math.ceil(events.length / 2);
      await this._flushBatch(endpoint, events.slice(0, mid));
      await this._flushBatch(endpoint, events.slice(mid));
      return;
    }
    await this._sendWithRetry(endpoint, events, context);
  }

  private async _flushNow(): Promise<void> {
    const logs = this._logQueue.splice(0, this._logQueue.length);
    const metrics = this._metricQueue.splice(0, this._metricQueue.length);
    this._softCapWarned = false;
    try {
      if (logs.length > 0) await this._flushBatch('/v1/logs', logs);
      if (metrics.length > 0) await this._flushBatch('/v1/metrics', metrics);
    } finally {
      this._metrics.last_flush_at_ms = this._clock.now();
    }
  }

  private _maybeFlushBySize(): void {
    if (this._logQueue.length + this._metricQueue.length >= FLUSH_SIZE_TRIGGER) void this.flush();
  }

  /**
   * Timer-driven flush loop: sleeps `_flushIntervalMs`, then flushes when the queue is non-empty.
   * Guarantees a low-traffic session (fewer than `FLUSH_SIZE_TRIGGER` events, no ERROR log, no quit)
   * still ships its telemetry. Started from the constructor when `_flushIntervalMs > 0`; stopped by `dispose()`.
   */
  private async _periodicFlushLoop(): Promise<void> {
    while (this._periodicFlushActive) {
      await this._clock.sleep(this._flushIntervalMs);
      if (!this._periodicFlushActive) return;
      if (this._logQueue.length + this._metricQueue.length === 0) continue;
      try {
        await this.flush();
      } catch {
        /** Best-effort: transport-level errors are already handled (retry/drop) inside `flush`. */
      }
    }
  }

  /**
   * Internal recorder used by `counter`/`gauge`/`histogram` after type narrowing.
   * @param name - Metric name (must exist in the registry).
   * @param type - Metric type discriminator.
   * @param value - The metric value.
   * @param labels - Optional labels.
   */
  private _recordMetric(
    name: string,
    type: MetricType,
    value: number,
    labels: Record<string, string> | undefined
  ): void {
    const entry = this._registry.metrics[name];
    if (!entry) {
      this._rateLimitedLogger.warn(
        'telemetry-agent:unknown-metric',
        `[TelemetryAgent] Unknown metric "${name}"; dropping.`
      );
      this._metrics.dropped_event_count += 1;
      return;
    }
    const event: MetricEvent = {
      name,
      ts: this._clock.now(),
      type,
      value,
      ...(labels ? { labels } : {})
    };
    if (!this._enqueueMetric(event)) return;
    void this._maybeFlushBySize();
  }

  private _scheduleErrorFlush(): void {
    if (this._errorFlushScheduled) return;
    this._errorFlushScheduled = true;
    void this._clock.sleep(ERROR_FLUSH_DELAY_MS).then(() => {
      this._errorFlushScheduled = false;
      void this.flush();
    });
  }

  /**
   * Issue the transport call with exponential-backoff retry (1,2,4,8,16,30s capped at 5min total).
   * Drops the batch and increments `dropped_event_count` when the retry budget is exhausted.
   * @param endpoint - Wire endpoint discriminator.
   * @param events - The batch payload.
   * @param context - The agent context snapshot to send alongside.
   */
  private async _sendWithRetry<E extends LogEvent | MetricEvent>(
    endpoint: '/v1/logs' | '/v1/metrics',
    events: E[],
    context: AgentContext
  ): Promise<void> {
    this._metrics.in_flight_request_count += 1;
    const start = this._clock.now();
    let attempt = 0;
    let lastError: unknown = null;
    try {
      while (true) {
        try {
          if (endpoint === '/v1/logs')
            await this._transport.send('/v1/logs', { context, events: events as LogEvent[] });
          else await this._transport.send('/v1/metrics', { context, events: events as MetricEvent[] });
          return;
        } catch (err: unknown) {
          lastError = err;
          /**
           * A typed transport error flagged non-retryable (eg: a 4xx poison batch) is terminal:
           * drop immediately rather than burning the retry budget. Non-`TelemetryTransportError`
           * throws (eg: network failures) fall through to the backoff logic and stay retryable.
           */
          if (err instanceof TelemetryTransportError && !err.retryable) {
            this._metrics.dropped_event_count += events.length;
            this._rateLimitedLogger.warn(
              'telemetry-agent:non-retryable',
              `[TelemetryAgent] Dropping ${events.length} events on ${endpoint}; non-retryable status ${err.statusCode}.`
            );
            return;
          }
          const elapsed = this._clock.now() - start;
          if (elapsed >= RETRY_TOTAL_CAP_MS) {
            this._metrics.dropped_event_count += events.length;
            this._rateLimitedLogger.warn(
              'telemetry-agent:retry-exhausted',
              `[TelemetryAgent] Retry budget exhausted on ${endpoint}; dropping ${events.length} events. Last error: ${String(lastError)}`
            );
            return;
          }
          const delay = BACKOFF_SEQUENCE_MS[Math.min(attempt, BACKOFF_SEQUENCE_MS.length - 1)];
          attempt += 1;
          const remaining = RETRY_TOTAL_CAP_MS - elapsed;
          const sleepFor = Math.min(delay, remaining);
          await this._clock.sleep(sleepFor);
        }
      }
    } finally {
      this._metrics.in_flight_request_count -= 1;
    }
  }

  private _startPeriodicFlush(): void {
    if (this._periodicFlushActive) return;
    this._periodicFlushActive = true;
    void this._periodicFlushLoop();
  }

  private _updateWaterMark(): void {
    const total = this._logQueue.length + this._metricQueue.length;
    if (total > this._metrics.queue_high_water_mark) this._metrics.queue_high_water_mark = total;
  }
}
